<script setup lang="ts">
import { onMounted, computed } from 'vue'
import { RouterView, RouterLink, useRoute } from 'vue-router'
import { useCatalogueStore } from '@/stores/catalogue'
import { useAuthStore } from '@/stores/auth'
import { formatRemaining } from '@/lib/claims'

const catalogue = useCatalogueStore()
const auth = useAuthStore()
const route = useRoute()

onMounted(() => catalogue.subscribePublic())

const showMfaBanner = computed(() => auth.canAdminister && auth.mfaRemaining < 30 * 60_000)
const remaining = computed(() => formatRemaining(auth.mfaRemaining))
</script>

<template>
  <header class="topbar">
    <div class="wrap bar">
      <RouterLink to="/" class="brand">
        <img class="mark" src="/brand/emblem.webp" width="36" height="36" alt=""
             aria-hidden="true" decoding="async" />
        <span class="names">
          <strong>ElectroCell</strong>
          <small>Solutions (Pvt) Ltd</small>
        </span>
      </RouterLink>

      <nav class="nav">
        <RouterLink to="/stock" class="btn btn-ghost btn-sm">Stock</RouterLink>
        <RouterLink v-if="auth.canAdminister" to="/admin" class="btn btn-ghost btn-sm">Admin</RouterLink>
        <RouterLink v-else to="/login" class="btn btn-ghost btn-sm">Sign in</RouterLink>
      </nav>
    </div>
  </header>

  <p v-if="showMfaBanner" class="mfa-banner">
    Your admin session expires in {{ remaining }}. You will be asked for a code again.
  </p>

  <p v-if="catalogue.error" class="load-error" role="alert">{{ catalogue.error }}</p>

  <main :key="route.path">
    <RouterView />
  </main>

  <footer class="wrap foot">
    <img class="footmark" src="/brand/logo.webp" width="76" height="79"
         alt="ElectroCell — EV batteries and parts" decoding="async" loading="lazy" />
    <p class="reach">
      <a href="tel:+94777406774" class="mono">+94 77 740 6774</a>
      <a href="mailto:electrocellsolutionslk@gmail.com" class="mono">electrocellsolutionslk@gmail.com</a>
    </p>
    <span>ElectroCell Solutions (Private) Limited · 3/1 Biyagama Road, Colombo, Sri Lanka</span>
    <span>Nissan, Leaf, Toyota and Aqua are trademarks of their respective owners. We are an independent importer.</span>
  </footer>
</template>

<style scoped>
.topbar{position:sticky;top:0;z-index:40;background:color-mix(in srgb,var(--bg) 90%,transparent);
  backdrop-filter:saturate(1.6) blur(10px);border-bottom:1px solid var(--line)}
.bar{display:flex;align-items:center;gap:12px;height:60px}
.brand{display:flex;align-items:center;gap:10px;margin-right:auto;text-decoration:none;color:inherit;min-width:0}
.mark{width:36px;height:36px;flex:none;border-radius:9px;background:#fff;border:1px solid var(--line);
  object-fit:contain;padding:2px}
.names strong{display:block;font-family:var(--f-display);font-weight:800;font-size:16.5px;letter-spacing:-.025em}
.names small{display:block;font-family:var(--f-mono);font-size:9px;color:var(--muted);letter-spacing:.11em;text-transform:uppercase}
@media (max-width:420px){.names small{display:none}}
.nav{display:flex;gap:4px}
.mfa-banner{background:var(--warn-soft);color:var(--warn);text-align:center;padding:8px 16px;font-size:13px}
.load-error{background:var(--bad-soft);color:var(--bad);text-align:center;padding:10px 16px;font-size:13.5px}
.foot{padding-block:24px 44px;border-top:1px solid var(--line);margin-top:40px;color:var(--muted);
  font-size:12.5px;display:flex;flex-direction:column;gap:5px}
.footmark{width:76px;height:auto;background:#fff;border:1px solid var(--line);border-radius:var(--r-sm);
  padding:6px;margin-bottom:10px}
.reach{display:flex;gap:18px;flex-wrap:wrap;margin-bottom:4px}
.reach a{font-size:14px;font-weight:600;color:var(--accent);text-decoration:none}
.reach a:hover{text-decoration:underline}
</style>
